// kanjo/Maintenance.jsx — service-provider directory (plumbers, HVAC, hood
// cleaning, …) with optional business-card photo, owner rating, and a
// per-provider opt-in to share with other restaurants in the area.
//
// Storage: local Dexie table `maintenanceProviders` (v9). Photo lives as a
// Blob on the row. The shared-with-area pool is server-side and not yet
// wired — the toggle is captured locally so it ships in the first push when
// the endpoint lands.

const CATEGORIES = [
  { id: 'plumber',       labelKey: 'maintenance.catPlumber',      label: 'Plumber' },
  { id: 'hvac',          labelKey: 'maintenance.catHvac',         label: 'HVAC' },
  { id: 'hood',          labelKey: 'maintenance.catHood',         label: 'Hood / vent cleaning' },
  { id: 'electrician',   labelKey: 'maintenance.catElectrician',  label: 'Electrician' },
  { id: 'refrigeration', labelKey: 'maintenance.catRefrigeration', label: 'Refrigeration' },
  { id: 'pest',          labelKey: 'maintenance.catPest',         label: 'Pest control' },
  { id: 'cleaning',      labelKey: 'maintenance.catCleaning',     label: 'Cleaning' },
  { id: 'grease',        labelKey: 'maintenance.catGrease',       label: 'Grease trap' },
  { id: 'linen',         labelKey: 'maintenance.catLinen',        label: 'Linen service' },
  { id: 'knives',        labelKey: 'maintenance.catKnives',       label: 'Knife sharpening' },
  { id: 'appliances',    labelKey: 'maintenance.catAppliances',   label: 'Appliances' },
  { id: 'utensils',      labelKey: 'maintenance.catUtensils',     label: 'Utensils' },
  { id: 'other',         labelKey: 'maintenance.catOther',        label: 'Other' },
];

function catLabel(id) {
  const meta = CATEGORIES.find((c) => c.id === id) || { labelKey: 'maintenance.catOther', label: 'Other' };
  return t(meta.labelKey, meta.label);
}

// ── Star rating ───────────────────────────────────────────────────────────
function StarRating({ value, onChange, size = 18, readOnly = false }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div style={{ display: 'inline-flex', gap: 2 }} role={readOnly ? 'img' : 'radiogroup'}
      aria-label={`${value || 0} of 5 stars`}>
      {stars.map((n) => {
        const filled = (value || 0) >= n;
        return (
          <button key={n}
            onClick={() => onChange && onChange(n === value ? 0 : n)}
            disabled={readOnly}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: readOnly ? 'default' : 'pointer',
              color: filled ? 'var(--warning,#e0a23b)' : 'var(--ink-3,#bbb)', lineHeight: 0 }}>
            <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'}
              stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
              <path d="M12 3.5l2.7 5.4 6 .9-4.4 4.2 1.1 6-5.4-2.9-5.4 2.9 1.1-6L3.3 9.8l6-.9z"/>
            </svg>
          </button>
        );
      })}
    </div>
  );
}

function useProviders() {
  const [st, setSt] = React.useState({ loading: true, rows: [] });
  const reload = React.useCallback(async () => {
    try {
      const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
      if (!claims) { setSt({ loading: false, rows: [] }); return; }
      const rows = await window.kanjo.db.maintenanceProviders
        .where('userId').equals(claims.sub).toArray();
      setSt({ loading: false, rows });
    } catch (e) {
      setSt({ loading: false, rows: [], error: String(e?.message || e) });
    }
  }, []);
  React.useEffect(() => { reload(); }, [reload]);
  return [st, reload];
}

// Small pill that flags a provider as shared into the area pool. A people
// glyph + "Shared" reads in any locale that hasn't translated the label yet
// and stays compact next to a long provider name.
function SharedBadge() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
      padding: '2px 8px 2px 6px', borderRadius: 999,
      background: 'var(--positive-soft,#e6f3ec)', color: 'var(--positive,#2e9e5b)',
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.02em',
      border: '1px solid rgba(46,158,91,0.25)' }}
      aria-label={t('maintenance.sharedBadge', 'Shared with area')}
      title={t('maintenance.sharedBadgeTitle', 'You chose to share this provider with other restaurants in your area.')}>
      <svg width="10" height="10" viewBox="0 0 14 14" fill="currentColor">
        <path d="M5 6.2a1.7 1.7 0 100-3.4 1.7 1.7 0 000 3.4zm5 0a1.7 1.7 0 100-3.4 1.7 1.7 0 000 3.4zM1.6 11c0-1.4 1.5-2.4 3.4-2.4S8.4 9.6 8.4 11v.6H1.6V11zm7.5 0c0-.8-.4-1.5-1-2 1.6.1 2.9 1 2.9 2v.6H9.1V11z"/>
      </svg>
      {t('maintenance.sharedShort', 'Shared')}
    </span>
  );
}

// ── Provider list — accordion of categories, no card thumbnails ──────────
// Each category is a large tappable card with a count badge. Tap to expand
// the list of providers beneath. Multiple categories can be open at once.
// Defaults to all collapsed; if only one category has providers, it opens
// automatically (no one likes a single shut accordion).
function MaintenanceMenu({ dark = false, persona = 'owner' }) {
  useT();
  const [st] = useProviders();
  const nav = React.useContext(KanjoNav);
  const cardFileRef = React.useRef(null);
  const [cfgOpen, setCfgOpen] = React.useState(false);
  // Free-tier ratings gate (Phase 3): free users see the contacts without
  // the rating column. Default true (paid behavior) until claims resolve
  // so paid users don't see a flash of star-less rows.
  const [showRatings, setShowRatings] = React.useState(true);
  // OCR quota chip (Phase 4): "X scans left this week" near the Scan CTA.
  // null while resolving — chip is hidden until we have a real number.
  const [quotaChip, setQuotaChip] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
        const tier = claims ? String(claims['custom:tier'] || 'free').toLowerCase() : null;
        const f = window.kanjo?.scale?.freeTier?.showsRating;
        if (live && f && tier !== null) setShowRatings(!!f(tier));

        const can = await window.kanjo?.scale?.plan?.canScan?.(0);
        if (live && can) {
          if (can.metered) {
            setQuotaChip({ text: 'Pay-as-you-go · billed per scan', kind: 'info' });
          } else if (typeof can.remaining === 'number') {
            const rem = can.remaining;
            if (rem <= 0) setQuotaChip({ text: '0 scans left this week · upgrade for more', kind: 'warn' });
            else if (rem <= 2) setQuotaChip({ text: `${rem} scan${rem === 1 ? '' : 's'} left this week`, kind: 'warn' });
            else setQuotaChip({ text: `${rem} scans left this week`, kind: 'info' });
          }
        }
      } catch { /* keep defaults */ }
    })();
    return () => { live = false; };
  }, []);

  // Native file/camera picker → stash the blob globally + jump to the Add
  // form, which auto-scans on mount. Mirrors how Receipts opens a scan
  // session, except a business card is one-sided so we skip the multi-page
  // capture UI and go straight to the form once we have the photo.
  const onCardPicked = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    window.__kanjoCardScan = f;
    nav.navigate('/maintenance/add');
  };
  const openCardScanner = () => { if (cardFileRef.current) cardFileRef.current.click(); };

  const byCat = React.useMemo(() => {
    const m = new Map();
    for (const r of st.rows) {
      if (!m.has(r.category)) m.set(r.category, []);
      m.get(r.category).push(r);
    }
    for (const list of m.values()) list.sort((a, b) => (b.rating || 0) - (a.rating || 0) || a.name.localeCompare(b.name));
    return m;
  }, [st.rows]);

  const nonEmptyCats = React.useMemo(() => CATEGORIES.filter((c) => (byCat.get(c.id) || []).length > 0), [byCat]);
  const [openCats, setOpenCats] = React.useState(() => new Set());
  // Auto-open the only category when there's one.
  React.useEffect(() => {
    if (nonEmptyCats.length === 1) setOpenCats(new Set([nonEmptyCats[0].id]));
  }, [nonEmptyCats.length]);
  const toggle = (id) => setOpenCats((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <KShell dark={dark} tab="maintenance" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back={false} title={t('header.maintenance', 'Maintenance')}
          subtitle={t('sub.maintenance', 'Plumbers, HVAC, hood cleaning…')}
          right={<><GearButton label={t('account.maintKeywords', 'Maintenance keywords')} onClick={() => setCfgOpen(true)} /><PersonaChip persona={persona}/></>}/>
        <Sheet open={cfgOpen} onClose={() => setCfgOpen(false)} title={t('account.maintKeywords', 'Maintenance keywords')}>
          <MaintenanceKeywordSettings />
        </Sheet>

        <div style={{ padding: '0 20px 18px' }}>
          {/* Primary action — opens the device camera/file picker directly,
              matching the Receipts "Scan" CTA. The native file dialog already
              exposes both "Take photo" and "Choose file" on mobile via the
              capture hint, so there's no intermediate screen. */}
          <input ref={cardFileRef} type="file" accept="image/*" capture="environment" onChange={onCardPicked}
            style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
              overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}/>
          <button onClick={openCardScanner}
            style={{ width: '100%', padding: '20px 22px', border: 'none', background: 'var(--brand-fill)',
              color: 'var(--on-brand-fill)', borderRadius: 16, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.14)',
              color: 'var(--on-brand-fill)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {/* camera glyph */}
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8.2a2 2 0 012-2h1.5l1.4-1.8a1.4 1.4 0 011.1-.5h4l1.1.5 1.4 1.8H17a2 2 0 012 2v8.6a2 2 0 01-2 2H5a2 2 0 01-2-2V8.2z"/>
                <circle cx="11" cy="12.5" r="3.4"/>
              </svg>
            </div>
            <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{t('maintenance.scanCta', 'Scan a business card')}</div>
              <div style={{ fontSize: 12.5, color: 'var(--on-brand-fill)', marginTop: 2 }}>
                {t('maintenance.scanHint', 'Take a photo or upload an image of the card')}
              </div>
            </div>
          </button>
          {/* Phase 4 quota chip — surfaces remaining scans for non-metered
              tiers. Hidden during resolution. */}
          {quotaChip && (
            <div role="status" style={{
              marginTop: 10,
              padding: '8px 12px',
              fontSize: 12.5,
              fontWeight: 500,
              borderRadius: 8,
              background: quotaChip.kind === 'warn' ? 'rgba(210, 86, 63, 0.10)' : 'var(--surface-2,#f5f5f7)',
              color: quotaChip.kind === 'warn' ? 'var(--danger,#d2563f)' : 'var(--ink-2,#666)',
              textAlign: 'center',
            }}>{quotaChip.text}</div>
          )}
          {/* Secondary: enter by hand for cards you don't have a photo of. */}
          <KLink to="/maintenance/add" asButton>
            <button style={{ width: '100%', marginTop: 10, padding: '11px 14px', border: '1px solid var(--rule)',
              background: 'var(--surface)', color: 'var(--ink-2)', borderRadius: 12,
              fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {t('maintenance.addByHand', 'Enter details by hand')}
            </button>
          </KLink>
        </div>

        {st.loading ? (
          <div style={{ color: 'var(--on-dim)', fontSize: 14, padding: 24, textAlign: 'center' }}>
            {t('account.loading', 'Loading…')}
          </div>
        ) : st.rows.length === 0 ? (
          <div style={{ padding: '0 20px' }}>
            <Empty title={t('maintenance.emptyTitle', 'No providers yet')}
              body={t('maintenance.emptyBody', "Add your plumber, HVAC tech, hood cleaner and the rest so you're never hunting through a drawer when the walk-in fails on a Saturday.")} />
          </div>
        ) : (
          <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {nonEmptyCats.map((cat) => {
              const list = byCat.get(cat.id);
              const open = openCats.has(cat.id);
              return (
                <div key={cat.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <button onClick={() => toggle(cat.id)} aria-expanded={open}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 12, padding: '16px 18px', background: 'transparent', border: 'none',
                      cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
                      {catLabel(cat.id)}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)',
                        padding: '3px 9px', borderRadius: 999, background: 'var(--surface-3,#f0f0f3)' }}>
                        {list.length}
                      </span>
                      <span style={{ display: 'inline-flex', color: 'var(--ink-3)',
                        transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
                        <Chev size={16}/>
                      </span>
                    </span>
                  </button>
                  {open && (
                    <div style={{ borderTop: '1px solid var(--rule)' }}>
                      {list.map((p, i) => (
                        <button key={p.providerId} onClick={() => nav.navigate(`/maintenance/detail?id=${p.providerId}`)}
                          style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center',
                            gap: 12, padding: '12px 18px', border: 'none', background: 'transparent',
                            borderTop: i > 0 ? '1px solid var(--hairline,#eee)' : 'none', cursor: 'pointer' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                              <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{p.name}</span>
                              {p.shareWithArea && <SharedBadge/>}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.phone || p.email || t('maintenance.noContact', 'No contact info')}
                            </div>
                          </div>
                          {showRatings && <StarRating value={p.rating} size={14} readOnly />}
                          <Chev/>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </KShell>
  );
}

// ── Cached blob → object URL hook, freed on unmount ───────────────────────
function useBlobUrl(blob) {
  const [url, setUrl] = React.useState(null);
  React.useEffect(() => {
    if (!blob) { setUrl(null); return; }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => { try { URL.revokeObjectURL(u); } catch {} };
  }, [blob]);
  return url;
}

// ── Add / edit form (shared) ─────────────────────────────────────────────
function ProviderForm({ initial, onSave, onDelete, busy, autoScanPhoto }) {
  useT();
  const [name, setName] = React.useState(initial?.name || '');
  const [category, setCategory] = React.useState(initial?.category || 'plumber');
  const [phone, setPhone] = React.useState(initial?.phone || '');
  const [email, setEmail] = React.useState(initial?.email || '');
  const [notes, setNotes] = React.useState(initial?.notes || '');
  const [rating, setRating] = React.useState(initial?.rating || 0);
  const [shareWithArea, setShareWithArea] = React.useState(!!initial?.shareWithArea);
  // Free-tier ratings gate (Phase 3): mirrors MaintenanceMenu. Default
  // true (paid behavior) so the field doesn't flash for paid users.
  const [showRatings, setShowRatings] = React.useState(true);
  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
        const tier = claims ? String(claims['custom:tier'] || 'free').toLowerCase() : null;
        const f = window.kanjo?.scale?.freeTier?.showsRating;
        if (live && f && tier !== null) setShowRatings(!!f(tier));
      } catch { /* keep default */ }
    })();
    return () => { live = false; };
  }, []);
  // Seed the photo from initial (edit flow) or autoScanPhoto (scan flow from
  // the menu CTA) so the picture renders immediately on mount.
  const [photo, setPhoto] = React.useState(initial?.photo || autoScanPhoto || null); // Blob
  const photoUrl = useBlobUrl(photo);
  const [scan, setScan] = React.useState({ phase: autoScanPhoto ? 'reading' : 'idle' });

  // Trigger the receipt-OCR pipeline with kind:'businesscard' so the server
  // runs the card-specific prompt and writes to businesscard-ocr/<id>.json.
  // Prefill only fields the user hasn't already typed — never clobber edits.
  const scanCard = async (blob) => {
    setScan({ phase: 'reading' });
    try {
      const mime = blob.type && /^image\/(jpeg|png|webp)$/.test(blob.type) ? blob.type : 'image/jpeg';
      const { receiptId } = await window.kanjo.ocr.uploadReceiptImage(blob, mime, { kind: 'businesscard' });
      const result = await window.kanjo.ocr.pollOcrResult(receiptId, { kind: 'businesscard', timeoutMs: 45_000 });
      const r = result || {};
      const headline = r.companyName || r.personName;
      if (headline && !name.trim()) setName(r.personName ? `${r.companyName || ''}${r.companyName ? ' — ' : ''}${r.personName}` : r.companyName);
      if (r.phone && !phone.trim()) setPhone(r.phone);
      if (r.email && !email.trim()) setEmail(String(r.email).toLowerCase());
      // Compose notes from title / website / address / extra notes if the
      // user hasn't filled them in yet.
      if (!notes.trim()) {
        const bits = [r.title, r.website, r.address, r.notes].filter(Boolean);
        if (bits.length) setNotes(bits.join('\n'));
      }
      setScan({ phase: 'done' });
    } catch (e) {
      console.warn('[maintenance] OCR failed', e);
      setScan({ phase: 'error', message: String(e?.message || e) });
    }
  };

  // When the user landed here via "Scan a business card" from the menu, the
  // blob is already in state and we fire OCR once on mount. The empty deps
  // intentionally keep this from re-firing on subsequent renders.
  React.useEffect(() => {
    if (autoScanPhoto) scanCard(autoScanPhoto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fileRef = React.useRef(null);
  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setPhoto(f);
    e.target.value = ''; // allow re-picking same file
    scanCard(f);
  };
  const pickFile = () => { if (fileRef.current) fileRef.current.click(); };
  const rescan = () => { if (photo) scanCard(photo); };

  const canSave = name.trim().length > 0 && !busy;
  const submit = () => onSave && onSave({
    name: name.trim(), category, phone: phone.trim(), email: email.trim().toLowerCase(),
    notes: notes.trim(), rating, shareWithArea, photo,
  });

  const input = {
    width: '100%', padding: '11px 13px', borderRadius: 10,
    border: '1px solid var(--rule)', background: 'var(--surface)',
    color: 'var(--ink)', fontSize: 14, fontFamily: 'var(--sans)', boxSizing: 'border-box',
  };
  const label = { fontSize: 13, color: 'var(--on-soft)', fontWeight: 500, marginBottom: 6, display: 'block' };

  return (
    <div style={{ padding: '0 20px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Business card photo */}
      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: 12, overflow: 'hidden', flexShrink: 0,
            border: '1px solid var(--rule)', background: 'var(--surface-3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)' }}>
            {photoUrl
              ? <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
              : <svg width="28" height="28" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="16" height="11" rx="2"/><path d="M7 10h4M7 12h6"/></svg>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Native file input is hidden visually but stays interactive — a
                button proxies the click via a ref so label-forwarding quirks
                across iOS / Android browsers don't break the file picker. */}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile}
              style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
                overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}/>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button type="button" onClick={pickFile} disabled={scan.phase === 'reading'}
                style={{ padding: '9px 14px', borderRadius: 10,
                  border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink)',
                  fontSize: 13, fontWeight: 600, cursor: scan.phase === 'reading' ? 'default' : 'pointer',
                  opacity: scan.phase === 'reading' ? 0.6 : 1 }}>
                {photo ? t('maintenance.retakePhoto', 'Replace photo') : t('maintenance.takePhoto', 'Take a photo of the card')}
              </button>
              {/* Manual "Scan card" only shows when scanning hasn't already
                  succeeded — i.e. the auto-scan failed (error) or we're in a
                  weird idle state where the user attached a photo but OCR
                  never ran. After a successful scan it stays hidden since
                  the fields are already populated; replacing the photo
                  auto-rescans. */}
              {photo && (scan.phase === 'error' || scan.phase === 'idle') && (
                <button type="button" onClick={rescan}
                  style={{ padding: '9px 14px', borderRadius: 10,
                    border: '1px solid var(--accent,#5b8def)', background: 'var(--surface)',
                    color: 'var(--accent,#5b8def)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {scan.phase === 'error' ? t('maintenance.scanRetry', 'Try again') : t('maintenance.scanCard', 'Scan card')}
                </button>
              )}
              {photo && (
                <button type="button" onClick={() => { setPhoto(null); setScan({ phase: 'idle' }); }}
                  disabled={scan.phase === 'reading'}
                  style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: 12.5, cursor: 'pointer' }}>
                  {t('maintenance.removePhoto', 'Remove')}
                </button>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)', minHeight: 16 }}>
              {scan.phase === 'reading' && (
                <span style={{ color: 'var(--brand,#3a5fbf)' }}>
                  {t('maintenance.scanReading', 'Reading the card…')}
                </span>
              )}
              {scan.phase === 'error' && (
                <span style={{ color: 'var(--warning,#9a6700)' }}>
                  {t('maintenance.scanFailed', "Couldn't read the card — enter details by hand.")}
                </span>
              )}
              {scan.phase === 'done' && (
                <span style={{ color: 'var(--positive,#2e9e5b)' }}>
                  ✓ {t('maintenance.scanDone', 'Card read — review the fields below.')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <label style={label}>{t('maintenance.fieldName', 'Name')}</label>
        <input style={input} value={name} onChange={(e) => setName(e.target.value)}
          placeholder={t('maintenance.fieldNamePh', 'Acme Plumbing — Joe')}
          autoFocus={!initial}/>
      </div>

      <div>
        <label style={label}>{t('maintenance.fieldCategory', 'Category')}</label>
        <select style={input} value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>{catLabel(c.id)}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={label}>{t('maintenance.fieldPhone', 'Phone')}</label>
          <input style={input} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 555-5555"/>
        </div>
        <div>
          <label style={label}>{t('maintenance.fieldEmail', 'Email')}</label>
          <input style={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="joe@acme.com"/>
        </div>
      </div>

      {showRatings && (
        <div>
          <label style={label}>{t('maintenance.fieldRating', 'Your rating')}</label>
          <StarRating value={rating} onChange={setRating} size={24}/>
        </div>
      )}

      <div>
        <label style={label}>{t('maintenance.fieldNotes', 'Notes')}</label>
        <textarea style={{ ...input, minHeight: 80, resize: 'vertical', lineHeight: 1.5 }}
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder={t('maintenance.fieldNotesPh', 'Hours, weekend rate, who he prefers to speak with…')}/>
      </div>

      {/* Share with area — opt-in sliding toggle. Matches the switch pattern
          used in Account / Admin: label + hint on the left, a 44×26 track on
          the right whose knob slides on. Top-aligned because the hint wraps
          to a few lines. */}
      <button onClick={() => setShareWithArea(!shareWithArea)}
        role="switch" aria-checked={shareWithArea}
        style={{ width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 14, textAlign: 'left', padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
          background: 'var(--surface-2,#f5f5f7)',
          border: '1px solid ' + (shareWithArea ? 'var(--accent,#5b8def)' : 'var(--hairline,#ddd)') }}>
        <span style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.45, flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 600 }}>{t('maintenance.shareLabel', 'Share with other restaurants in my area')}</span>
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-3)', marginTop: 3 }}>
            {t('maintenance.shareHint', "We'll add this provider + your rating to a shared directory for restaurants near your ZIP. Your business name is never shown or attached to this service provider.")}
          </span>
        </span>
        <span aria-hidden="true" style={{ width: 44, height: 26, borderRadius: 13, flexShrink: 0, marginTop: 1,
          background: shareWithArea ? 'var(--accent,#5b8def)' : 'var(--hairline,#ccc)',
          position: 'relative', transition: 'background .15s' }}>
          <span style={{ position: 'absolute', top: 3, left: shareWithArea ? 21 : 3, width: 20, height: 20,
            borderRadius: 10, background: '#fff', transition: 'left .15s' }}/>
        </span>
      </button>

      <button onClick={submit} disabled={!canSave}
        className="btn primary lg"
        style={{ width: '100%', cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.5 }}>
        {busy ? t('account.saving', 'Saving…') : initial ? t('maintenance.saveChanges', 'Save changes') : t('maintenance.savePrimary', 'Save provider')}
      </button>

      {onDelete && (
        <button onClick={onDelete} disabled={busy}
          style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1px solid var(--hairline)',
            background: 'var(--surface)', color: 'var(--danger)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          {t('maintenance.deleteProvider', 'Delete provider')}
        </button>
      )}
    </div>
  );
}

// ── Add screen ────────────────────────────────────────────────────────────
function MaintenanceAdd({ dark = false, persona = 'owner' }) {
  useT();
  const nav = React.useContext(KanjoNav);
  const [busy, setBusy] = React.useState(false);
  // The menu's "Scan a business card" CTA stashes the chosen blob globally
  // (avoids round-tripping a Blob through the hash router). Pick it up once
  // on mount; clearing the stash prevents a subsequent visit from re-using
  // an old image.
  const [autoScan] = React.useState(() => {
    const blob = window.__kanjoCardScan instanceof Blob ? window.__kanjoCardScan : null;
    window.__kanjoCardScan = null;
    return blob;
  });
  const onSave = async (patch) => {
    setBusy(true);
    try {
      const claims = await window.kanjo.auth.currentClaims();
      if (!claims) throw new Error('not signed in');
      const row = { providerId: crypto.randomUUID(), userId: claims.sub, createdAt: Date.now(), ...patch };
      await window.kanjo.db.maintenanceProviders.put(row);
      nav.navigate('/maintenance');
    } catch (e) {
      console.warn('[maintenance] save failed', e);
      setBusy(false);
    }
  };
  return (
    <KShell dark={dark} tab="maintenance" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <KHeader back title={t('maintenance.addTitle', 'Add a provider')} />
        <ProviderForm onSave={onSave} busy={busy} autoScanPhoto={autoScan}/>
      </div>
    </KShell>
  );
}

// ── Detail screen ─────────────────────────────────────────────────────────
function MaintenanceDetail({ dark = false, persona = 'owner' }) {
  useT();
  const nav = React.useContext(KanjoNav);
  const id = useHashQuery('id');
  const [row, setRow] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [missing, setMissing] = React.useState(false);
  React.useEffect(() => {
    if (!id) { setMissing(true); return; }
    (async () => {
      const r = await window.kanjo.db.maintenanceProviders.get(id);
      if (!r) { setMissing(true); return; }
      setRow(r);
    })();
  }, [id]);

  const onSave = async (patch) => {
    setBusy(true);
    try {
      await window.kanjo.db.maintenanceProviders.put({ ...row, ...patch, updatedAt: Date.now() });
      nav.navigate('/maintenance');
    } catch (e) { console.warn('[maintenance] update failed', e); setBusy(false); }
  };
  const onDelete = async () => {
    if (!window.confirm(t('maintenance.deleteConfirm', 'Delete this provider?'))) return;
    setBusy(true);
    try {
      await window.kanjo.db.maintenanceProviders.delete(id);
      nav.navigate('/maintenance');
    } catch (e) { console.warn('[maintenance] delete failed', e); setBusy(false); }
  };

  return (
    <KShell dark={dark} tab="maintenance" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <KHeader back title={row?.name || t('maintenance.detailTitle', 'Provider')} />
        {missing ? <Empty title={t('maintenance.notFound', 'Provider not found')} body={t('maintenance.notFoundBody', 'It may have been removed.')} />
          : !row ? <div style={{ padding: 24, color: 'var(--on-dim)' }}>{t('account.loading', 'Loading…')}</div>
          : <ProviderForm initial={row} onSave={onSave} onDelete={onDelete} busy={busy}/>}
      </div>
    </KShell>
  );
}

Object.assign(window, { MaintenanceMenu, MaintenanceAdd, MaintenanceDetail });
